import { cloneElement, useId, type ReactElement } from "react";
import styles from "./Field.module.css";

type Controllable = ReactElement<{
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}>;

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: Controllable;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  children,
}: FieldProps): ReactElement {
  const id = useId();
  const messageId = `${id}-message`;
  const describedBy = error || hint ? messageId : undefined;

  const control = cloneElement(children, {
    id,
    "aria-describedby": describedBy,
    "aria-invalid": error ? true : children.props["aria-invalid"],
  });

  let message: ReactElement | null = null;
  if (error) {
    message = (
      <p id={messageId} className={styles.error} role="alert">
        {error}
      </p>
    );
  } else if (hint) {
    message = (
      <p id={messageId} className={styles.hint}>
        {hint}
      </p>
    );
  }

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {control}
      {message}
    </div>
  );
}
